/**
 * Owner: Masa Bayu
 * File: qxp-package.ts
 * Date:Time: 2026-09-14 20:33
 * Description: 28A QXP Core package — immutable publication contract (≠ QXW SoT).
 */
import { createQXPId } from './id';
import { TIMESTAMP_PATTERN, VERSION_PATTERN, assertOptionalTrimmed, assertValidId } from './guards';
import { QXP_CORE_MESSAGE_KEYS, QXP_CORE_MODEL_VERSION, QXP_FORMAT, QXP_VERSION, } from './qxp-codes';
import { QXP_PACKAGE_ID_PREFIX, createQXPAccessibilityRef, createQXPAssetRef, createQXPContentRef, createQXPDesignRef, createQXPIntegrityMetadata, createQXPLayoutRef, createQXPMetadataRef, createQXPNavigationRef, createQXPPublicationIdentity, createQXPStructureRef, normalizeQXPDiagnostics, } from './qxp-parts';
/**
 * QXP Core is the native publication artifact contract.
 * QXW = Authoring Truth · QXP = Native Publication Truth · QXReader = Reading Runtime.
 */
export const QXP_CORE_ATTACHMENT = 'qxp-native-publication';
export const QXP_PACKAGE_FORBIDDEN_KEYS = [
    'bytes',
    'buffer',
    'blob',
    'zip',
    'dom',
    'DOM',
    'document',
    'react',
    'css',
    'renderer',
    'runtime',
    'reader',
    'ui',
    'marketplace',
    'purchase',
    'license',
    'cdn',
    'transport',
    'encryption',
    'paginate',
    'pagination',
    'qxwContent',
    'content',
    'body',
    'mutateQxw',
    'editor',
    'selection',
    'history',
    'execute',
    'browser',
    'window',
];
export const QXP_PACKAGE_ALLOWED_KEYS = [
    'packageId',
    'format',
    'version',
    'modelVersion',
    'publicationIdentity',
    'contentRefs',
    'structureRefs',
    'layoutRefs',
    'designRefs',
    'assetRefs',
    'navigationRefs',
    'accessibilityRefs',
    'metadataRefs',
    'integrity',
    'diagnostics',
    'label',
    'timestamps',
];
function nowIso() {
    return new Date().toISOString();
}
function coreIssue(code, path, message) {
    return Object.freeze({
        code,
        path,
        messageKey: QXP_CORE_MESSAGE_KEYS[code],
        message,
    });
}
function assertNoLeakage(value, path) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`${path} must be an object`);
    }
    const record = value;
    for (const key of Object.keys(record)) {
        if (QXP_PACKAGE_FORBIDDEN_KEYS.includes(key)) {
            throw new Error(`QXP package must not include forbidden field "${key}"`);
        }
    }
}
function collectRefIds(packageValue) {
    return [
        ...packageValue.contentRefs.map((r) => r.refId),
        ...packageValue.structureRefs.map((r) => r.refId),
        ...packageValue.layoutRefs.map((r) => r.refId),
        ...packageValue.designRefs.map((r) => r.refId),
        ...packageValue.assetRefs.map((r) => r.refId),
        ...packageValue.navigationRefs.map((r) => r.refId),
        ...packageValue.accessibilityRefs.map((r) => r.refId),
        ...packageValue.metadataRefs.map((r) => r.entryId),
    ];
}
/**
 * Create an immutable QXP package identity with publication references.
 * Does not mutate QXW, render, or attach Reader/marketplace concerns.
 */
export function createQXPPackage(options = {}) {
    assertNoLeakage(options, 'QXP package options');
    if (options.format !== undefined && options.format !== QXP_FORMAT) {
        throw new Error(`QXP package format must be "${QXP_FORMAT}"`);
    }
    if (options.version !== undefined && options.version !== QXP_VERSION) {
        throw new Error(`QXP package version must be "${QXP_VERSION}"`);
    }
    const modelVersion = options.modelVersion ?? QXP_CORE_MODEL_VERSION;
    if (typeof modelVersion !== 'string' || !VERSION_PATTERN.test(modelVersion)) {
        throw new Error('QXP package modelVersion must be a semver string');
    }
    if (modelVersion !== QXP_CORE_MODEL_VERSION) {
        throw new Error(`QXP package modelVersion must be "${QXP_CORE_MODEL_VERSION}"`);
    }
    const packageId = options.packageId !== undefined ? options.packageId : createQXPId(QXP_PACKAGE_ID_PREFIX);
    assertValidId(packageId, 'QXP packageId');
    const createdAt = options.timestamps?.createdAt ?? nowIso();
    const updatedAt = options.timestamps?.updatedAt ?? createdAt;
    if (!TIMESTAMP_PATTERN.test(createdAt) || !TIMESTAMP_PATTERN.test(updatedAt)) {
        throw new Error('QXP package timestamps must be ISO-8601 strings');
    }
    const publicationIdentity = createQXPPublicationIdentity(options.publicationIdentity);
    const contentRefs = Object.freeze((options.contentRefs ?? []).map((ref) => createQXPContentRef(ref)));
    const structureRefs = Object.freeze((options.structureRefs ?? []).map((ref) => createQXPStructureRef(ref)));
    const layoutRefs = Object.freeze((options.layoutRefs ?? []).map((ref) => createQXPLayoutRef(ref)));
    const designRefs = Object.freeze((options.designRefs ?? []).map((ref) => createQXPDesignRef(ref)));
    const assetRefs = Object.freeze((options.assetRefs ?? []).map((ref) => createQXPAssetRef(ref)));
    const navigationRefs = Object.freeze((options.navigationRefs ?? []).map((ref) => createQXPNavigationRef(ref)));
    const accessibilityRefs = Object.freeze((options.accessibilityRefs ?? []).map((ref) => createQXPAccessibilityRef(ref)));
    const metadataRefs = Object.freeze((options.metadataRefs ?? []).map((ref) => createQXPMetadataRef(ref)));
    const integrity = createQXPIntegrityMetadata(options.integrity);
    const diagnostics = normalizeQXPDiagnostics(options.diagnostics);
    const pkg = Object.freeze({
        packageId,
        format: QXP_FORMAT,
        version: QXP_VERSION,
        modelVersion: QXP_CORE_MODEL_VERSION,
        publicationIdentity,
        contentRefs,
        structureRefs,
        layoutRefs,
        designRefs,
        assetRefs,
        navigationRefs,
        accessibilityRefs,
        metadataRefs,
        integrity,
        diagnostics,
        label: assertOptionalTrimmed(options.label, 'QXP package label'),
        timestamps: Object.freeze({ createdAt, updatedAt }),
    });
    const validation = validateQXPPackage(pkg);
    if (!validation.valid) {
        const first = validation.errors[0];
        throw new Error(first?.message ?? 'QXP package validation failed');
    }
    return pkg;
}
/**
 * Report-only validation of a QXP package.
 * Validation ≠ Repair · QXP ≠ QXW authoring model.
 */
export function validateQXPPackage(value) {
    const errors = [];
    const warnings = [];
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return Object.freeze({
            valid: false,
            errors: Object.freeze([
                coreIssue('QXP_PACKAGE_REQUIRED', 'package', 'QXP package must be an object'),
            ]),
            warnings: Object.freeze([]),
        });
    }
    const record = value;
    for (const key of Object.keys(record)) {
        if (QXP_PACKAGE_FORBIDDEN_KEYS.includes(key)) {
            errors.push(coreIssue('QXP_LEAKAGE', key, `Forbidden field "${key}" is not allowed on QXP package`));
        }
    }
    const allowed = new Set(QXP_PACKAGE_ALLOWED_KEYS);
    for (const key of Object.keys(record)) {
        if (!allowed.has(key) && !QXP_PACKAGE_FORBIDDEN_KEYS.includes(key)) {
            errors.push(coreIssue('QXP_LEAKAGE', key, `Unknown field "${key}" is not allowed`));
        }
    }
    if (record.format !== QXP_FORMAT) {
        errors.push(coreIssue('QXP_FORMAT_INVALID', 'format', `format must be "${QXP_FORMAT}"`));
    }
    if (record.version !== QXP_VERSION) {
        errors.push(coreIssue('QXP_VERSION_UNSUPPORTED', 'version', `version must be "${QXP_VERSION}"`));
    }
    if (record.modelVersion !== QXP_CORE_MODEL_VERSION) {
        errors.push(coreIssue('QXP_VERSION_UNSUPPORTED', 'modelVersion', `modelVersion must be "${QXP_CORE_MODEL_VERSION}"`));
    }
    try {
        assertValidId(record.packageId, 'QXP packageId');
    }
    catch (error) {
        errors.push(coreIssue('QXP_PACKAGE_REQUIRED', 'packageId', error instanceof Error ? error.message : String(error)));
    }
    const identity = record.publicationIdentity;
    if (typeof identity !== 'object' || identity === null || Array.isArray(identity)) {
        errors.push(coreIssue('QXP_PUBLICATION_REQUIRED', 'publicationIdentity', 'publicationIdentity is required'));
    }
    else {
        try {
            createQXPPublicationIdentity(identity);
        }
        catch (error) {
            errors.push(coreIssue('QXP_PUBLICATION_REQUIRED', 'publicationIdentity', error instanceof Error ? error.message : String(error)));
        }
    }
    if (errors.length === 0) {
        const pkg = value;
        const ids = collectRefIds(pkg);
        const seen = new Set();
        for (const id of ids) {
            if (seen.has(id)) {
                errors.push(coreIssue('QXP_REF_DUPLICATE', id, `Duplicate QXP ref id "${id}"`));
            }
            seen.add(id);
        }
        const structureIds = new Set(pkg.structureRefs.map((r) => r.refId));
        for (const [index, ref] of pkg.structureRefs.entries()) {
            if (ref.parentRefId !== null && !structureIds.has(ref.parentRefId)) {
                errors.push(coreIssue('QXP_REF_KIND_UNKNOWN', `structureRefs[${String(index)}].parentRefId`, `Unknown structure parentRefId "${ref.parentRefId}"`));
            }
        }
        if (pkg.integrity.compiledAt !== null && !TIMESTAMP_PATTERN.test(pkg.integrity.compiledAt)) {
            errors.push(coreIssue('QXP_INTEGRITY_INVALID', 'integrity.compiledAt', 'integrity.compiledAt must be an ISO-8601 timestamp'));
        }
    }
    return Object.freeze({
        valid: errors.length === 0,
        errors: Object.freeze(errors),
        warnings: Object.freeze(warnings),
    });
}
export function isQXPPackage(value) {
    return validateQXPPackage(value).valid;
}
export function clearQXPPackage(value) {
    if (!isQXPPackage(value)) {
        throw new Error('clearQXPPackage requires a valid QXP package');
    }
    return null;
}
//# sourceMappingURL=qxp-package.js.map